import React, { useState } from 'react'
import { AlertTriangle, Loader2, Calendar, XCircle } from 'lucide-react'
import Modal from '../ui/Modal'
import { Button } from '../ui'
import { useToast } from '../../contexts/ToastContext'
import { subscriptionService, Subscription } from '../../services/subscriptionService'
import { useAuthStore } from '../../stores/authStore'

interface CancelSubscriptionModalProps {
  isOpen: boolean
  onClose: () => void
  onCancelSuccess: () => void
  subscription: Subscription | null
}

const CANCELLATION_REASONS = [
  { value: 'too_expensive', label: 'Too expensive' },
  { value: 'missing_features', label: 'Missing features I need' },
  { value: 'not_using', label: 'Not using it enough' },
  { value: 'switching_competitor', label: 'Switching to another service' },
  { value: 'temporary', label: 'Only needed temporarily' },
  { value: 'other', label: 'Other' }
]

const CancelSubscriptionModal: React.FC<CancelSubscriptionModalProps> = ({
  isOpen,
  onClose,
  onCancelSuccess,
  subscription
}) => {
  const { showError, showSuccess } = useToast()
  const { logout } = useAuthStore()

  const [step, setStep] = useState<'confirm' | 'reason'>('confirm')
  const [cancelImmediately, setCancelImmediately] = useState(false)
  const [selectedReason, setSelectedReason] = useState('')
  const [otherReason, setOtherReason] = useState('')
  const [cancelling, setCancelling] = useState(false)

  const formatDate = (dateString: string | null) => {
    if (!dateString) return ''
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    })
  }

  const handleClose = () => {
    // Reset state when closing
    setStep('confirm')
    setCancelImmediately(false)
    setSelectedReason('')
    setOtherReason('')
    onClose()
  }

  const handleProceedToReason = () => {
    setStep('reason')
  }

  const handleCancel = async () => {
    setCancelling(true)
    try {
      const reason = selectedReason === 'other' ? otherReason : selectedReason

      const response = await subscriptionService.cancelSubscription({
        cancelImmediately,
        reason: reason || undefined
      })

      if (response.success) {
        const message = cancelImmediately
          ? 'Your subscription has been canceled. You will be logged out.'
          : `Your subscription will remain active until ${formatDate(response.data.endsAt)}.`

        showSuccess('Subscription Canceled', message)

        if (cancelImmediately) {
          // Log user out and clear all sessions for immediate cancellation
          setTimeout(async () => {
            await logout()
          }, 1500) // Brief delay to show success message
        } else {
          onCancelSuccess()
          handleClose()
        }
      } else {
        showError('Cancellation Failed', response.message || 'Please try again.')
      }
    } catch (error) {
      console.error('Error canceling subscription:', error)
      showError('Cancellation Failed', 'An unexpected error occurred. Please try again.')
    } finally {
      setCancelling(false)
    }
  }

  if (!subscription) return null

  const isTrialing = subscription.status === 'trialing'
  const periodEndDate = formatDate(subscription.current_period_end)

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={step === 'confirm' ? 'Cancel Subscription' : 'Help us improve'}
      size="md"
    >
      {step === 'confirm' ? (
        <div className="space-y-4">
          {/* Warning Banner */}
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-red-100 rounded-lg flex-shrink-0">
                <AlertTriangle className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-red-800">
                  Are you sure you want to cancel?
                </h3>
                <p className="text-sm text-red-700 mt-1">
                  {isTrialing
                    ? 'Your free trial will end and you will lose access to all features.'
                    : 'You will lose access to your current plan features.'}
                </p>
              </div>
            </div>
          </div>

          {/* Current Plan Info */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wider">Current Plan</p>
                <p className="text-base font-semibold text-gray-900 mt-0.5">
                  {subscription.tier_display_name}
                </p>
              </div>
              <div className="text-right">
                <p className="text-xs text-gray-500 uppercase tracking-wider">
                  {isTrialing ? 'Trial Ends' : 'Billing Period Ends'}
                </p>
                <p className="text-sm font-medium text-gray-900 mt-0.5">
                  {isTrialing ? formatDate(subscription.trial_end) : periodEndDate}
                </p>
              </div>
            </div>
          </div>

          {/* Cancellation Options */}
          <div className="space-y-3">
            <p className="text-sm font-medium text-gray-700">When should the cancellation take effect?</p>

            {/* End of Period Option */}
            <label
              className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                !cancelImmediately
                  ? 'border-avi-primary bg-avi-primary/5 ring-1 ring-avi-primary'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <input
                type="radio"
                name="cancelTiming"
                checked={!cancelImmediately}
                onChange={() => setCancelImmediately(false)}
                className="mt-0.5 h-4 w-4 text-avi-primary border-gray-300 focus:ring-avi-primary"
              />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-gray-500" />
                  <span className="text-sm font-medium text-gray-900">
                    At end of billing period
                  </span>
                  <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded font-medium">
                    Recommended
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-1 ml-6">
                  Keep access until {periodEndDate}. No further charges.
                </p>
              </div>
            </label>

            {/* Immediate Option */}
            <label
              className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                cancelImmediately
                  ? 'border-red-300 bg-red-50 ring-1 ring-red-300'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <input
                type="radio"
                name="cancelTiming"
                checked={cancelImmediately}
                onChange={() => setCancelImmediately(true)}
                className="mt-0.5 h-4 w-4 text-red-600 border-gray-300 focus:ring-red-500"
              />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <XCircle className="h-4 w-4 text-red-500" />
                  <span className="text-sm font-medium text-gray-900">
                    Cancel immediately
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-1 ml-6">
                  Lose access right away. No refund for remaining time.
                </p>
              </div>
            </label>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-4 border-t border-gray-100">
            <Button variant="outline" size="sm" onClick={handleClose}>
              Keep Subscription
            </Button>
            <Button
              variant="danger"
              size="sm"
              onClick={handleProceedToReason}
            >
              Continue
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Reason Selection */}
          <div>
            <p className="text-sm text-gray-600 mb-3">
              Before you go, please tell us why you're canceling. This helps us improve.
            </p>

            <div className="space-y-2">
              {CANCELLATION_REASONS.map((reason) => (
                <label
                  key={reason.value}
                  className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                    selectedReason === reason.value
                      ? 'border-avi-primary bg-avi-primary/5'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="cancellationReason"
                    value={reason.value}
                    checked={selectedReason === reason.value}
                    onChange={(e) => setSelectedReason(e.target.value)}
                    className="h-4 w-4 text-avi-primary border-gray-300 focus:ring-avi-primary"
                  />
                  <span className="text-sm text-gray-900">{reason.label}</span>
                </label>
              ))}
            </div>

            {/* Other Reason Text Input */}
            {selectedReason === 'other' && (
              <textarea
                value={otherReason}
                onChange={(e) => setOtherReason(e.target.value)}
                placeholder="Please tell us more..."
                className="mt-3 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-avi-primary focus:border-avi-primary"
                rows={3}
              />
            )}
          </div>

          {/* Summary */}
          <div className="bg-gray-50 rounded-lg p-3">
            <p className="text-xs text-gray-500">
              {cancelImmediately ? (
                <span className="text-red-600 font-medium">
                  Your subscription will be canceled immediately and you will lose access right away.
                </span>
              ) : (
                <>
                  Your subscription will remain active until{' '}
                  <span className="font-medium text-gray-900">{periodEndDate}</span>.
                  You won't be charged again.
                </>
              )}
            </p>
          </div>

          {/* Actions */}
          <div className="flex justify-between pt-4 border-t border-gray-100">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setStep('confirm')}
              disabled={cancelling}
            >
              Back
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={handleClose} disabled={cancelling}>
                Keep Subscription
              </Button>
              <Button
                variant="danger"
                size="sm"
                onClick={handleCancel}
                disabled={cancelling}
              >
                {cancelling ? (
                  <>
                    <Loader2 className="w-3 h-3 animate-spin mr-1.5" />
                    Canceling...
                  </>
                ) : (
                  'Cancel Subscription'
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </Modal>
  )
}

export default CancelSubscriptionModal
